import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import ReactSelect from "react-select";
import Swal from "sweetalert2";

/**
 * Human gate for rows the engine could not resolve to a single emission factor.
 *
 * Shown after a run when one or more rows come back with status "NeedsReview".
 * Each row offers ranked candidates (parsed from candidatesJson); the user picks one and confirms.
 * Confirming persists the choice so it is reused automatically on later runs.
 *
 * Variants:
 *   "pgs"   (default) Purchased Goods & Services — candidates are activity ids, POST /ConfirmActivityMapping
 *                     with { userEmail, rowId, chosenActivityId }. Behaviour unchanged.
 *   "waste"           Scope 3 waste — candidates are disposal methods (activity_id = method, title = label),
 *                     plus an "AddSupplierFactor" action that reveals a small factor form. POST /ConfirmWasteMapping
 *                     with { userEmail, rowId, chosenDisposalMethod } or { userEmail, rowId, supplierFactor }.
 *
 * Props:
 *   show            - boolean, modal visibility
 *   rows            - array of NeedsReview rows (run response successfulResults, or the review queue)
 *   userEmail       - logged-in user's email (localStorage userName)
 *   onClose         - () => void
 *   onAllResolved   - () => void, called once every row has been confirmed
 *   variant         - "pgs" | "waste" (default "pgs")
 *   confirmEndpoint - proxy route to post confirmations to (default depends on variant)
 */
const ADD_SUPPLIER_FACTOR = "AddSupplierFactor";

function ReviewGateModal({ show, rows, userEmail, onClose, onAllResolved, variant = "pgs", confirmEndpoint }) {
  const isWaste = variant === "waste";
  const endpoint = confirmEndpoint || (isWaste ? "ConfirmWasteMapping" : "ConfirmActivityMapping");

  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState({}); // rowId -> activity_id
  const [supplierInputs, setSupplierInputs] = useState({}); // rowId -> { factorKgPerTonne, source, year, gwpBasis }
  const [submitting, setSubmitting] = useState(false);

  // Parse candidatesJson once per row and seed default selection to the auto-picked activity.
  useEffect(() => {
    const parsed = (rows || []).map((r) => {
      let candidates = [];
      try {
        candidates = JSON.parse(r.candidatesJson || "[]") || [];
      } catch (e) {
        candidates = [];
      }
      if ((!candidates || candidates.length === 0) && Array.isArray(r.candidates)) {
        candidates = r.candidates.map((c) => ({ activity_id: c.activity_id || c.activityId, title: c.title, ...c }));
      }
      return { ...r, candidates };
    });
    setPending(parsed);

    const defaults = {};
    parsed.forEach((r) => {
      defaults[r.id] = r.activityId || (r.candidates[0] && r.candidates[0].activity_id) || "";
    });
    setSelected(defaults);
    setSupplierInputs({});
  }, [rows]);

  const optionsFor = (row) =>
    (row.candidates || []).map((c) =>
      isWaste
        ? { value: c.activity_id, label: c.title || c.activity_id }
        : { value: c.activity_id, label: `${c.title} — ${Math.round((c.score || 0) * 100)}% · ${c.region || "—"}` }
    );

  const quantityLabel = (row) => {
    if (row.quantity && Number(row.quantity) > 0) return `${row.quantity} ${row.unit || ""}`.trim();
    if (row.cost && Number(row.cost) > 0) return `${row.cost} ${row.costCurrency || ""}`.trim();
    return "—";
  };

  const rowTitle = (row) => {
    if (!isWaste) return row.productOrServiceName || "(unnamed)";
    const desc = row.wasteItemDescription ? ` — ${row.wasteItemDescription}` : "";
    return `${row.wasteType || "(unknown waste type)"}${desc}`;
  };

  const rowSubtitle = (row) => {
    if (!isWaste) {
      return (
        <>
          {row.supplierName ? `Supplier: ${row.supplierName} · ` : ""}
          Qty/Cost: {quantityLabel(row)} · Provisional: {row.emissions || "0"} tCO2e
          {row.matchConfidence != null ? ` · confidence ${Math.round(row.matchConfidence * 100)}%` : ""}
        </>
      );
    }
    const tonnes = row.quantityTonnes != null ? `${Number(row.quantityTonnes).toLocaleString()} t` : quantityLabel(row);
    return (
      <>
        {row.reportPeriod ? `${row.reportPeriod} · ` : ""}
        {row.siteLocation ? `${row.siteLocation} · ` : ""}
        {tonnes}
        {row.disposalMethodRaw ? ` · uploaded method: "${row.disposalMethodRaw}"` : ""}
        {row.wasteManagementPartner ? ` · partner: ${row.wasteManagementPartner}` : ""}
        {row.statusReason ? <div style={{ marginTop: 2 }}>{row.statusReason}</div> : null}
      </>
    );
  };

  const supplierInput = (rowId, field) => (supplierInputs[rowId] && supplierInputs[rowId][field]) || "";
  const setSupplierInput = (rowId, field, value) =>
    setSupplierInputs((prev) => ({ ...prev, [rowId]: { ...(prev[rowId] || {}), [field]: value } }));

  function buildBody(row) {
    const chosen = selected[row.id];
    if (!isWaste) return { userEmail, rowId: row.id, chosenActivityId: chosen };
    if (chosen === ADD_SUPPLIER_FACTOR) {
      const factor = Number(supplierInput(row.id, "factorKgPerTonne"));
      if (!(factor > 0)) return { error: "Enter a supplier factor greater than zero (kg CO2e per tonne)." };
      if (!row.wasteManagementPartner) return { error: "This row has no waste management partner; choose a disposal method instead." };
      return {
        userEmail,
        rowId: row.id,
        supplierFactor: {
          factorKgPerTonne: factor,
          source: supplierInput(row.id, "source") || null,
          year: supplierInput(row.id, "year") || null,
          gwpBasis: supplierInput(row.id, "gwpBasis") || null,
        },
      };
    }
    return { userEmail, rowId: row.id, chosenDisposalMethod: chosen };
  }

  async function confirmRow(row) {
    const chosen = selected[row.id];
    if (!chosen) {
      Swal.fire({ icon: "warning", text: isWaste ? "Please select a disposal method for this row." : "Please select an activity for this row." });
      return null;
    }
    const body = buildBody(row);
    if (body.error) {
      Swal.fire({ icon: "warning", text: body.error });
      return null;
    }
    const resp = await fetch(`${process.env.REACT_APP_BASE_API_URL}${endpoint}`, {
      method: "POST",
      headers: { "Content-type": "application/json", username: userEmail },
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    return data;
  }

  const isResolvedStatus = (status) => {
    const s = (status || "").toLowerCase();
    return s === "matched" || s === "confirmed";
  };

  async function handleConfirmAll() {
    setSubmitting(true);
    const stillPending = [];
    let confirmedCount = 0;
    const failures = [];

    for (const row of pending) {
      try {
        const data = await confirmRow(row);
        const updated = data && data.data;
        const status = updated && (updated.status || (updated.row && updated.row.Status));
        if (data && data.success !== false && updated && isResolvedStatus(status)) {
          confirmedCount += 1;
        } else {
          failures.push(rowTitle(row) || row.id);
          stillPending.push(row);
        }
      } catch (e) {
        failures.push(rowTitle(row) || row.id);
        stillPending.push(row);
      }
    }

    setSubmitting(false);
    setPending(stillPending);

    if (stillPending.length === 0) {
      Swal.fire({
        icon: "success",
        iconHtml: '<img src="assets/img/successicon.png" style="width: 55px; height: 55px;" />',
        html: `<h4 style="color:#407775;font-size:20px;">${isWaste ? "Rows confirmed" : "Mappings confirmed"}</h4><p style="font-size:16px;">${confirmedCount} row(s) confirmed and saved for reuse.</p>`,
      });
      onAllResolved && onAllResolved();
    } else {
      Swal.fire({
        icon: "warning",
        html: `<h4 style="color:#f8bb86;font-size:20px;">Partially confirmed</h4><p style="font-size:15px;">Confirmed ${confirmedCount}. Could not confirm: ${failures.join(", ")}</p>`,
      });
    }
  }

  return (
    <Modal show={show} onHide={onClose} size="lg" centered className="reports-modal">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "20px", color: "#407775" }}>
          {isWaste ? "Review waste rows without an emission factor" : "Review emission factor matches"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ fontSize: "14px", color: "#707070", marginBottom: "16px" }}>
          {isWaste
            ? "No emission factor could be resolved for these rows. Pick the disposal method that applies, or add the factor your waste partner supplied — your choice is saved and reused for this company next time."
            : "These rows matched more than one possible emission factor. Pick the correct activity for each, then confirm — your choice is saved and reused automatically next time."}
        </p>
        {pending.length === 0 ? (
          <p style={{ fontSize: "14px", color: "#407775" }}>All rows resolved.</p>
        ) : (
          <div style={{ maxHeight: "420px", overflowY: "auto" }}>
            {pending.map((row) => (
              <div
                key={row.id}
                style={{
                  border: "1px solid #40777526",
                  borderRadius: "6px",
                  padding: "12px",
                  marginBottom: "12px",
                }}
              >
                <div style={{ fontSize: "15px", fontWeight: 600, color: "#333" }}>{rowTitle(row)}</div>
                <div style={{ fontSize: "12px", color: "#888", marginBottom: "8px" }}>{rowSubtitle(row)}</div>
                <ReactSelect
                  inputId={`review-${row.id}`}
                  value={optionsFor(row).find((o) => o.value === selected[row.id]) || null}
                  onChange={(opt) => setSelected((prev) => ({ ...prev, [row.id]: opt ? opt.value : "" }))}
                  options={optionsFor(row)}
                  placeholder={isWaste ? "Select the disposal method…" : "Select the correct activity…"}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
                />
                {isWaste && selected[row.id] === ADD_SUPPLIER_FACTOR && (
                  <div className="row g-2 mt-2" style={{ fontSize: "13px" }}>
                    <div className="col-6 col-md-3">
                      <Form.Label style={{ marginBottom: 2 }}>kg CO2e / tonne *</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        step="any"
                        value={supplierInput(row.id, "factorKgPerTonne")}
                        onChange={(e) => setSupplierInput(row.id, "factorKgPerTonne", e.target.value)}
                      />
                    </div>
                    <div className="col-6 col-md-4">
                      <Form.Label style={{ marginBottom: 2 }}>Source (EPD, LCA, certificate)</Form.Label>
                      <Form.Control size="sm" value={supplierInput(row.id, "source")} onChange={(e) => setSupplierInput(row.id, "source", e.target.value)} />
                    </div>
                    <div className="col-6 col-md-2">
                      <Form.Label style={{ marginBottom: 2 }}>Year</Form.Label>
                      <Form.Control size="sm" value={supplierInput(row.id, "year")} onChange={(e) => setSupplierInput(row.id, "year", e.target.value)} />
                    </div>
                    <div className="col-6 col-md-3">
                      <Form.Label style={{ marginBottom: 2 }}>GWP basis</Form.Label>
                      <Form.Select size="sm" value={supplierInput(row.id, "gwpBasis")} onChange={(e) => setSupplierInput(row.id, "gwpBasis", e.target.value)}>
                        <option value="">—</option>
                        <option value="AR5">AR5</option>
                        <option value="AR6">AR6</option>
                        <option value="AR4">AR4</option>
                      </Form.Select>
                    </div>
                    <div className="col-12" style={{ color: "#888", fontSize: "12px" }}>
                      Saved as a supplier-specific factor for {row.wasteManagementPartner || "this partner"} and {row.wasteType || "this waste type"}; requires the EsgAdmin role.
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>
          Close
        </Button>
        <Button variant="primary" onClick={handleConfirmAll} disabled={submitting || pending.length === 0}>
          {submitting ? "Confirming…" : isWaste ? "Confirm rows" : "Confirm mappings"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ReviewGateModal;
